/**
 * Admin — Newsletter subscribers.
 * Lists Firestore `newsletter/*` docs and lets admins filter by `lang`
 * and `createdAt` range, export CSV, and delete individual entries.
 *
 * Access model mirrors /admin/users:
 *   - `useUserAuth().isAdmin` OR local-admin login (`useAuth().isAuthed`)
 *   - Non-admins are redirected to "/".
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Mail,
  Search,
  Loader2,
  Trash2,
  Download,
  RefreshCw,
  Filter,
  Globe,
} from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { useUserAuth } from "@/lib/user-auth";
import { useAuth } from "@/lib/auth";
import { useI18n, t as tt } from "@/lib/i18n";
import {
  adminDeleteNewsletter,
  adminListNewsletter,
  newsletterToCsv,
  type NewsletterSubscriber,
} from "@/lib/newsletter-admin";

export const Route = createFileRoute("/admin/newsletter")({
  head: () => ({
    meta: [
      { title: "إدارة النشرة البريدية — Rawan UI" },
      { name: "description", content: "قائمة مشتركي النشرة البريدية مع تصفية حسب اللغة والتاريخ." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminNewsletterPage,
});

type LangFilter = "all" | "ar" | "en" | "fr";

function AdminNewsletterPage() {
  const { language } = useI18n();
  const t = (ar: string, en: string, fr: string) => tt(language, ar, en, fr);

  const { user, isAdmin: fbAdmin, loading: authLoading } = useUserAuth();
  const { isAuthed: localAdmin } = useAuth();
  const isAdmin = fbAdmin || localAdmin;
  const navigate = useNavigate();

  const [rows, setRows] = useState<NewsletterSubscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [lang, setLang] = useState<LangFilter>("all");
  const [from, setFrom] = useState<string>(""); // yyyy-mm-dd
  const [to, setTo] = useState<string>("");     // yyyy-mm-dd

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      void navigate({ to: "/" });
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, isAdmin]);

  async function refresh() {
    setLoading(true);
    try {
      const list = await adminListNewsletter(1000);
      setRows(list);
    } finally {
      setLoading(false);
    }
  }

  async function remove(row: NewsletterSubscriber) {
    if (!confirm(t(
      `حذف المشترك ${row.email}؟`,
      `Delete subscriber ${row.email}?`,
      `Supprimer l'abonné ${row.email} ?`,
    ))) return;
    await adminDeleteNewsletter(row.id);
    setRows((arr) => arr.filter((x) => x.id !== row.id));
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
    return rows.filter((r) => {
      if (lang !== "all" && r.lang !== lang) return false;
      if (needle && !r.email.toLowerCase().includes(needle)) return false;
      const t = r.createdAt?.getTime() ?? 0;
      if (fromMs !== null && t < fromMs) return false;
      if (toMs !== null && t > toMs) return false;
      return true;
    });
  }, [rows, q, lang, from, to]);

  const counts = useMemo(() => {
    const c = { total: rows.length, ar: 0, en: 0, fr: 0 };
    for (const r of rows) {
      if (r.lang === "ar") c.ar++;
      else if (r.lang === "en") c.en++;
      else if (r.lang === "fr") c.fr++;
    }
    return c;
  }, [rows]);

  function exportCsv() {
    const csv = newsletterToCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function resetFilters() {
    setQ("");
    setLang("all");
    setFrom("");
    setTo("");
  }

  if (authLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isAdmin) return null;

  return (
    <div className="min-h-dvh flex flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1 mx-auto w-full max-w-6xl px-4 py-6 space-y-6">
        <BackToDashboard />

        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Mail className="h-6 w-6" />
              {t("مشتركو النشرة البريدية", "Newsletter subscribers", "Abonnés à la newsletter")}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t(
                "قائمة المشتركين مع تصفية حسب اللغة وتاريخ الاشتراك.",
                "Subscriber list with filters by language and subscription date.",
                "Liste des abonnés avec filtres par langue et date d'inscription.",
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refresh}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm hover:bg-accent"
            >
              <RefreshCw className="h-4 w-4" />
              {t("تحديث", "Refresh", "Actualiser")}
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!filtered.length}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              CSV
            </button>
          </div>
        </header>

        {/* Stats */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label={t("الإجمالي", "Total", "Total")} value={counts.total} />
          <StatCard label="AR" value={counts.ar} />
          <StatCard label="EN" value={counts.en} />
          <StatCard label="FR" value={counts.fr} />
        </section>

        {/* Filters */}
        <section className="rounded-lg border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Filter className="h-4 w-4" />
            {t("التصفية", "Filters", "Filtres")}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="text-sm space-y-1">
              <span className="text-xs text-muted-foreground">
                {t("بحث بالبريد", "Search email", "Rechercher un e-mail")}
              </span>
              <div className="relative">
                <Search className="absolute inset-y-0 start-2 my-auto h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="name@example.com"
                  className="w-full ps-8 pe-3 py-2 rounded-md border border-input bg-background text-sm"
                />
              </div>
            </label>
            <label className="text-sm space-y-1">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Globe className="h-3.5 w-3.5" />
                {t("اللغة", "Language", "Langue")}
              </span>
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value as LangFilter)}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
              >
                <option value="all">{t("الكل", "All", "Toutes")}</option>
                <option value="ar">العربية (AR)</option>
                <option value="en">English (EN)</option>
                <option value="fr">Français (FR)</option>
              </select>
            </label>
            <label className="text-sm space-y-1">
              <span className="text-xs text-muted-foreground">
                {t("من تاريخ", "From date", "Du")}
              </span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
              />
            </label>
            <label className="text-sm space-y-1">
              <span className="text-xs text-muted-foreground">
                {t("إلى تاريخ", "To date", "Au")}
              </span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
              />
            </label>
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-muted-foreground">
              {t("عدد النتائج", "Results", "Résultats")}: <b className="text-foreground">{filtered.length}</b>
            </span>
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-primary hover:underline"
            >
              {t("مسح التصفية", "Clear filters", "Réinitialiser")}
            </button>
          </div>
        </section>

        {/* Table */}
        <section className="rounded-lg border border-border bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-sm text-muted-foreground">
              {t("لا توجد نتائج مطابقة.", "No matching subscribers.", "Aucun abonné correspondant.")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-start p-3">{t("البريد", "Email", "E-mail")}</th>
                    <th className="text-start p-3">{t("اللغة", "Lang", "Langue")}</th>
                    <th className="text-start p-3">{t("المصدر", "Source", "Source")}</th>
                    <th className="text-start p-3">{t("تاريخ الاشتراك", "Subscribed at", "Date")}</th>
                    <th className="text-end p-3">{t("إجراءات", "Actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-accent/30">
                      <td className="p-3 font-medium break-all">{r.email}</td>
                      <td className="p-3">
                        <span className="inline-flex items-center rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5 uppercase">
                          {r.lang || "—"}
                        </span>
                      </td>
                      <td className="p-3 text-muted-foreground">{r.source ?? "—"}</td>
                      <td className="p-3 text-muted-foreground whitespace-nowrap">
                        {r.createdAt ? r.createdAt.toLocaleString() : "—"}
                      </td>
                      <td className="p-3 text-end">
                        <button
                          type="button"
                          onClick={() => remove(r)}
                          className="inline-flex items-center gap-1 rounded-md border border-destructive/30 text-destructive px-2 py-1 text-xs hover:bg-destructive/10"
                          aria-label={t("حذف", "Delete", "Supprimer")}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t("حذف", "Delete", "Supprimer")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold">{value.toLocaleString()}</div>
    </div>
  );
}
